import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Grid, Divider} from '@mui/material';
import Scrollbar from './Scrollbar';
import {flushSync} from 'react-dom'; // al inicio del archivo

const waitForScrollHeightStable = (container, callback, maxTries = 10, delay = 50) => {
    let tries = 0;
    let lastHeight = container.scrollHeight;

    const check = () => {
        const newHeight = container.scrollHeight;
        if (newHeight !== lastHeight) {
            lastHeight = newHeight;
            tries = 0;
            setTimeout(check, delay);
        } else if (tries < maxTries) {
            tries++;
            setTimeout(check, delay);
        } else {
            callback();  // Scroll height estable o se alcanzó el máximo
        }
    };

    setTimeout(check, delay);
};


const InfiniteScroller = (
    {
        fetchItems,
        renderItem,
        renderKey,
        pageSize = 20,
        maxItems = 40,
        itemHeight = 500,
        itemsPerRow = 4,
        dividerEvery = 8,
        header = null,
        renderLoader = (direction) => (
            <div style={{textAlign: 'center', padding: 8}}>
                {direction === 'up' ? '⬆️ Cargando más...' : '⬇️ Cargando más...'}
            </div>
        ),
        resetCounter = 0  // 👈 nuevo
    }
) => {

    console.log("InfiniteScroller")
    const scrollRef = useRef(null);
    const isLoadingRef = useRef(false);
    const [items, setItems] = useState([]);
    const [startOffset, setStartOffset] = useState(0);
    const [loadedOffsets, setLoadedOffsets] = useState(new Set());
    const [, setHasMoreAbove] = useState(true);
    const [hasMoreBelow, setHasMoreBelow] = useState(true);
    const [loadingDirection, setLoadingDirection] = useState(null);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    let justLoadedUp = false;

    const chunkArray = (array, size) => {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    };

    const loadDown = async () => {
        console.log("loadDown")
        const offset = startOffset + items.length;
        if (!hasMoreBelow || isLoadingRef.current || loadedOffsets.has(offset)) return;

        isLoadingRef.current = true;
        setLoadingDirection('down');

        try {
            const scrollContainer = scrollRef.current;
            const prevScrollTop = scrollContainer?.scrollTop;

            const newItems = await fetchItems(offset); // esperamos los datos

            if (newItems.length < pageSize) setHasMoreBelow(false);

            let combined = [...items, ...newItems];
            const loaded = new Set(loadedOffsets);
            loaded.add(offset);

            let removedHeight = 0;
            if (combined.length > maxItems) {
                const earliestOffset = Math.min(...loaded);
                const removedItems = combined.filter(item => item.__offset === earliestOffset);
                combined = combined.filter(item => item.__offset !== earliestOffset);
                loaded.delete(earliestOffset);
                removedHeight = (removedItems.length / itemsPerRow) * itemHeight;
                setStartOffset(earliestOffset + pageSize);
            }

            // 👇🏻 Forzamos render sincrónico antes de modificar el scroll
            flushSync(() => {
                setItems(combined);
                setLoadedOffsets(loaded);
            });

            setHasMoreAbove(true);

            // Solo cuando el render está forzado, movemos el scroll
            if (removedHeight > 0 && scrollContainer) {
                scrollContainer.scrollTop = Math.max(0, prevScrollTop - removedHeight);
            }

        } finally {
            isLoadingRef.current = false;
            setLoadingDirection(null);
        }
    };
    const loadUp = async () => {
        console.log("loadUp")
        const offset = Math.max(0, startOffset - pageSize);
        if (startOffset <= 0 || isLoadingRef.current || loadedOffsets.has(offset)) return;

        isLoadingRef.current = true;
        justLoadedUp = true;
        setLoadingDirection('up');

        const scrollContainer = scrollRef.current;
        const newItems = await fetchItems(offset);
        if (offset === 0) setHasMoreAbove(false);

        let combined = [...newItems, ...items];
        const loaded = new Set(loadedOffsets);
        loaded.add(offset);

        if (combined.length > maxItems) {
            const latestOffset = Math.max(...loaded);
            combined = combined.filter(item => item.__offset !== latestOffset);
            loaded.delete(latestOffset);
        }

        setItems(combined);
        setStartOffset(offset);
        setLoadedOffsets(loaded);
        setHasMoreBelow(true);

        waitForScrollHeightStable(scrollContainer, () => {
            const rowsAdded = newItems.length / itemsPerRow;
            const addedHeight = rowsAdded * itemHeight;
            scrollContainer.scrollTop = addedHeight;
        });

        // ⚠️ Desbloqueamos después de un tiempo prudente
        setTimeout(() => {
            justLoadedUp = false;
        }, 300);

        isLoadingRef.current = false;
        setLoadingDirection(null);
    };
    const handleScroll = (e) => {
        if (!initialLoadDone || isLoadingRef.current || justLoadedUp) return;

        const scrollContainer = e.target;
        const scrollTop = scrollContainer.scrollTop;
        const scrollHeight = scrollContainer.scrollHeight;
        const clientHeight = scrollContainer.clientHeight;
        const threshold = 200;

        if (scrollTop + clientHeight >= scrollHeight - threshold) {
            loadDown();
        } else if (scrollTop <= threshold) {
            loadUp();
        }
    };


    useEffect(() => {
        let isMounted = true;
        const init = async () => {
            console.log("init")
            const initialItems = await fetchItems(0);
            if (isMounted) {
                setItems(initialItems);
                setStartOffset(0);
                setLoadedOffsets(new Set([0]));
                setHasMoreAbove(false);
                setHasMoreBelow(initialItems.length >= pageSize);
                setInitialLoadDone(true); // ✅ habilita el scroll después del primer fetch
            }
        };
        console.log("let isMounted", isMounted)
        init();

        return () => {
            isMounted = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetCounter]);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        const checkScrollable = () => {
            if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
                setInitialLoadDone(true); // solo aquí permitimos el scroll
            } else {
                setTimeout(checkScrollable, 100); // esperamos hasta que sea scrollable
            }
        };

        checkScrollable();
    }, [items]);

    const chunks = chunkArray(items, itemsPerRow);

    return (
        <Grid container sx={{height: 'calc(100vh - 180px)'}}>
            {header && header}
            <Scrollbar
                ref={scrollRef}
                onScroll={handleScroll}
                style={{height: '100%', overflow: 'auto', width: '100%'}}
            >
                {loadingDirection === 'up' && renderLoader('up')}

                {chunks.map((chunk, index) => (
                    <React.Fragment key={`chunk-${index}`}>
                        <Grid container spacing={2}>
                            {chunk.map((item, i) => (
                                <Grid item xs={12} sm={12 / itemsPerRow} key={renderKey(item)}>
                                    {renderItem(item)}
                                </Grid>
                            ))}
                        </Grid>
                        {(dividerEvery && ((index + 1) * itemsPerRow) % dividerEvery === 0 && index !== chunks.length - 1) && (
                            <Divider sx={{my: 2}}/>
                        )}
                    </React.Fragment>
                ))}

                {loadingDirection === 'down' && renderLoader('down')}
            </Scrollbar>
        </Grid>
    );
};

InfiniteScroller.propTypes = {
    fetchItems: PropTypes.func.isRequired,
    renderItem: PropTypes.func.isRequired,
    renderKey: PropTypes.func.isRequired,
    pageSize: PropTypes.number,
    maxItems: PropTypes.number,
    itemHeight: PropTypes.number,
    itemsPerRow: PropTypes.number,
    dividerEvery: PropTypes.number,
    header: PropTypes.node,
    renderLoader: PropTypes.func,
    resetCounter: PropTypes.number,
};

export default InfiniteScroller;
